import { Component, Input, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { Page, Promotion, ContentItem, Question } from 'app/shared/services/app/content/models';
import { ImagePreloadService } from 'app/shared/services/image-preload/image-preload.service';

@Component({
  selector: 'tog-page-preview',
  templateUrl: 'page-preview.component.html',
  styleUrls: ['page-preview.component.scss']
})
export class PagePreviewComponent implements OnInit, OnDestroy {
  formPhase: number = 0;
  showTabs: boolean = true;
  promotion: Promotion;
  subpages: Page[] = [];
  content: ContentItem[] = [];
  questions: Question[] = [];
  title: string = '';
  description: string = '';

  // so that we can pause/play the video
  currentVideo: any;
  styleElement: HTMLStyleElement;

  // style helpers
  lessonContainerOffset: string = '';
  playButtonBackgroundColor: string = '';

  // optional
  headerColor: string = '';

  @Input() alwaysShowTabs: boolean = false;

  @Input() set forceFormPhase(index: number) {
    this.formPhase = index;
  }

  @Input() set previewTitle(title: string) {
    if (title) this.title = title;
    else this.title = '';
  }

  @Input() set previewDescription(description: string) {
    if (description) {
      this.description = description;
    } else this.description = '';
    setTimeout(() => { this.updateLessonContainerOffset(); });
  }

  @Input() set previewSubpages(pages: Page[]) {
    if (pages && pages.length) {
      this.subpages = pages;
      this.content = [];
      this.questions = [];
      this.createStyleTag();
      this.showTabs = false;
    } else this.subpages = [];
  }

  @Input() set previewContent(content: ContentItem[]) {
    if (content && content.length) {
      this.subpages = [];
      this.content = content;
      this.loadContentImages();
      this.createStyleTag();
      this.scrollToTopOfContainer();
    } else this.content = [];
    this.showOrHideTabs();
  }

  @Input() set previewQuestions(questions: Question[]) {
    if (questions && questions.length) {
      this.subpages = [];
      this.questions = questions;
      this.scrollToTopOfContainer();
    } else this.questions = [];
    this.showOrHideTabs();
  }

  @Input() set previewHeaderColor(color: string) {
    if (color) {
      this.headerColor = color;
      this.updatePlayButtonBackgroundColor();
      this.createStyleTag();
    }
  }

  @Input() set previewPromotion(promotion: Promotion) {
    if (promotion) {
      this.promotion = promotion;
    } else this.promotion = null;
  }

  @ViewChild('descriptionContainer') descriptionContainer: ElementRef;

  constructor(private _el: ElementRef, private _ips: ImagePreloadService) { }

  ngOnInit() {
    let self = this;
    document.addEventListener('webkitfullscreenchange', function() {
      Function.prototype.call(self, self.stopVideos());
    });

    document.addEventListener('mozfullscreenchange', function() {
      Function.prototype.call(self, self.stopVideos());
    });

    this._el.nativeElement.onclick = function(e) {
      if (e.target.classList.contains('video-container')) {
        let video = e.target.getElementsByTagName('video')[0];
        if (video) {
          video.webkitEnterFullScreen();
          self.currentVideo = video;
        }
      }
    };
  }

  get previewType() {
    if (this.content.length !== 1) return 'normal';
    else {
      if (this.content[0].type === 'spreadsheet') return 'spreadsheet';
      else if (this.content[0].type === 'pdf') return 'pdf';
      else return 'normal';
    }
  }

  loadContentImages() {
    let urls = [];
    this.content.forEach((contentItem: any) => {
      if (contentItem.type === 'video' && contentItem.thumbnail) {
        urls.push(contentItem.thumbnail);
      }
      if (contentItem.type === 'image') {
        urls.push(contentItem.imageUrl);
      }
    });
    this._ips.preloadImagesWithURLs(urls, 'pagePreview');
  }

  ngOnDestroy() {
    if (this.stopVideos) {
      document.removeEventListener('webkitfullscreenchange', this.stopVideos);
    }
    this.removeStyleElement();
  }

  scrollToTopOfContainer() {
    let previewBody = this._el.nativeElement.getElementsByClassName('app-preview-body');
    if (previewBody.length) {
      previewBody[0].scrollTop = 0;
    }
  }

  stopVideos() {
    if (this.currentVideo) {
      this.currentVideo.pause();
    }
  }

  showOrHideTabs() {
    if (this.alwaysShowTabs) this.showTabs = true;
    else if (this.questions && this.questions.length &&
        this.content && this.content.length) {
      this.showTabs = true;
      this.formPhase = 0;
    } else {
      this.formPhase = (this.content && this.content.length) ? 0 : 1;
      this.showTabs = false;
    }
    setTimeout(() => { this.updateLessonContainerOffset(); });
  }

  updateLessonContainerOffset() {
    let offset = 49;
    let descriptionContainer = this._el.nativeElement.getElementsByClassName('description');
    if (descriptionContainer.length) {
      offset += descriptionContainer[0].clientHeight;
    } if (this.showTabs) {
      offset += 31;
    }
    this.lessonContainerOffset = offset + 'px';
  }

  updatePlayButtonBackgroundColor() {
    let parsedHexValue = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(this.headerColor);
    let rgb = {
      r: parseInt(parsedHexValue[1], 16),
      g: parseInt(parsedHexValue[2], 16),
      b: parseInt(parsedHexValue[3], 16)
    };
    if (rgb) {
      this.playButtonBackgroundColor = 'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ', ' + '0.9)';
    } else this.playButtonBackgroundColor = '';
  }

  createStyleTag() {
    if (this.headerColor) {
      if (!this.styleElement) {
        this.styleElement = document.createElement('style');
        this.styleElement.type = 'text/css';
        let wrapper = this._el.nativeElement;
        wrapper.insertBefore(this.styleElement, wrapper.firstChild);
      }
      this.styleElement.innerHTML = [
        '.app-preview-body h1, .app-preview-body .title, ul li:before, ol li:before {',
          'color: ' + this.headerColor + ' !important;',
        '}'].join('');
    }
  }

  removeStyleElement() {
    if (this.styleElement) {
      let parent: Node = this.styleElement.parentNode;
      parent.removeChild(this.styleElement);
    }
  }

  switchTab(index: number) {
    this.formPhase = index;
    this.scrollToTopOfContainer();
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/page-preview/page-preview.component.ts